import { Component, OnInit, inject, signal } from '@angular/core';
import { DocumentNode } from 'graphql';
import { finalize, take } from 'rxjs/operators';
import { ApolloService } from '../../../core/services/apollo.service';
import { Notification } from '../../shared/entities/notification/notification';
import { NotificationRowActions } from '../../shared/services/notification/notification-list.store';
import { NotificationService } from '../../shared/services/notification/notification.service';
import * as fromGraphQl from '../../shared/services/notification/graphql';

/**
 * Prominente Ankuendigungen auf dem Dashboard (Client-Dok §8.6) — gerendert als app-notification,
 * dieselbe Zeile wie im Stream (Glocke, /notifications). Ein zweites Layout nur fuer diese fuenf
 * Zeilen zu pflegen zahlt sich nicht (Vorbild: dieselbe Entscheidung fuer notification.component).
 *
 * Bewusst kein NotificationListStore: die Query kennt weder Cursor noch Segment und liefert
 * serverseitig gedeckelt auf fuenf Zeilen. Aus demselben Grund kein Polling (Client-Dok §8.6) —
 * geladen wird einmal beim Betreten des Dashboards.
 */
@Component({
  selector: 'app-dashboard-announcements',
  templateUrl: './dashboard-announcements.component.html'
})
export class DashboardAnnouncementsComponent implements OnInit, NotificationRowActions {

  private readonly apolloService = inject(ApolloService);
  private readonly notificationService = inject(NotificationService);

  readonly items = signal<Notification[]>([]);

  /** Bindet sich selbst als [list] an app-notification — dieselbe Schnittstelle wie NotificationListStore. */
  readonly rowActions: NotificationRowActions = this;

  ngOnInit(): void {
    this.apolloService
      .query<fromGraphQl.DashboardAnnouncementsResponse>(
        fromGraphQl.DASHBOARD_ANNOUNCEMENTS_QUERY,
        {},
        { fetchPolicy: 'network-only' }
      )
      .pipe(take(1))
      .subscribe({
        next: (result) => this.items.set(result?.data?.getMyDashboardAnnouncements ?? []),
        // Die leere Liste ist der Normalfall (Client-Dok §8.6) — ein fehlgeschlagener Ladevorgang
        // sieht dann einfach genauso aus wie "kein Banner". Kein Fehlerzustand auf dem Dashboard.
        error: (error) => console.warn('dashboard-announcements: Laden fehlgeschlagen', error)
      });
  }

  /**
   * app-notification meldet hierueber ein Oeffnen (Details-Knopf oder externes Ziel). Client-Dok
   * §8.6-Tabelle: ein Oeffnen setzt readAt mit.
   */
  reportOpened(id: string): void {
    this.patchOrRemove(id, fromGraphQl.NOTIFICATION_OPENED_MUTATION, {
      id,
      channel: 'IN_APP' as fromGraphQl.NotificationChannelKey
    });
  }

  /** Haken in der Titelzeile: gelesen ohne zu oeffnen (Client-Dok §8.6-Tabelle) — bei sticky ausdruecklich erlaubt. */
  markRead(id: string): void {
    this.patchOrRemove(id, fromGraphQl.MARK_NOTIFICATION_READ_MUTATION, { id });
  }

  /**
   * Papierkorb: der staerkere Lesezustand. app-notification rendert diesen Knopf fuer sticky-Zeilen
   * gar nicht erst (der Server lehnt deleteNotification dafuer ohnehin ab) — hier deshalb immer
   * entfernen.
   */
  deleteNotification(id: string): void {
    this.items.update((items) => items.filter((item) => item.id !== id));
    this.apolloService
      .mutateObservable(fromGraphQl.DELETE_NOTIFICATION_MUTATION, { id })
      .pipe(take(1), finalize(() => this.notificationService.refetchCount()))
      .subscribe();
  }

  /**
   * Setzt readAt (Oeffnen oder "als gelesen markieren"). Bei einer gewoehnlichen Zeile verschwindet
   * das Banner damit sofort optimistisch. Bei sticky NICHT: der Server liefert die Zeile weiter,
   * solange ihr Anzeigefenster laeuft, egal ob sie gelesen ist (Client-Dok §8.6) — sie lokal zu
   * entfernen, waere eine Luege, die erst beim naechsten Laden auffliegt. Stattdessen wird readAt
   * lokal gepatcht, genau wie NotificationListStore.patchReadAt: app-notification zeigt den
   * Lesezustand dann ueber Balken/Titelgewicht/Dimmung an, auch bei einer stehenbleibenden Zeile.
   * Der Zaehler an der Glocke zieht in jedem Fall nach, unabhaengig vom Ausgang der Mutation.
   */
  private patchOrRemove(id: string, mutation: DocumentNode, variables: Record<string, unknown>): void {
    const item = this.items().find((candidate) => candidate.id === id);
    if (!item?.sticky) {
      this.items.update((items) => items.filter((candidate) => candidate.id !== id));
    } else {
      this.items.update((items) =>
        items.map((candidate) =>
          candidate.id === id
            ? { ...candidate, readAt: candidate.readAt ?? new Date().toISOString() }
            : candidate
        )
      );
    }
    this.apolloService
      .mutateObservable(mutation, variables)
      .pipe(take(1), finalize(() => this.notificationService.refetchCount()))
      .subscribe();
  }

}
